/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A2/components/A2Section.tsx.
 */

import React, { useEffect, useState } from "react";
import {
  FormField,
  FormSubSection,
  type FormDataResponse,
} from "../../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../../services/calorificValuesPublicService";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import "../FormA2.css";
import { A2_MAX_VEHICLES_PER_RAW_MATERIAL } from "../utils/a2VehicleLayout";
import { A2_MAX_FUELS_PER_RAW_MATERIAL } from "../utils/a2FuelLayout";
import type { VehicleCategoryEmissionFactorPublic } from "../../../../../../services/vehicleCategoryEmissionFactorsPublicService";
import { findVehicleCategoryEmissionFactor } from "../../../../../../utils/a2TransportCarbonCalculations";
import ConfirmModal from "../../../../../../components/common/ConfirmModal";
import FormFieldLabel from "../../../../../../components/common/FormFieldLabel";
import { resolveContributorFieldDisplayLabel } from "../../shared/contributorFieldDisplayLabels";
import { getContributorTextInputHtmlProps } from "../../../../../../utils/contributorFormFieldInputProps";
import { formatUnitOptionLabel } from "../../../../../../utils/formatUnitSymbol";
import {
  buildContributorUnitSelectOptions,
  resolveContributorDropdownDisplayLabel,
  resolveContributorUnitDisplayLabel,
  type ContributorUnitOption,
} from "../../shared/unitSelectOptions";
import {
  filterFuelDensityUnitOptionsToKgPerM3,
  isFuelDensityUnitFormFieldName,
} from "../../shared/fuelMixReferenceAutofill";
import { resolveContributorFieldMandatory } from "../../shared/fuelMixDensityValidation";
import {
  buildEmbodiedCarbonFunctionalUnitSelectState,
  isEmbodiedCarbonFunctionalUnitFieldName,
  resolveEmbodiedCarbonFunctionalUnitDisplayLabel,
} from "../../shared/contributorPerFunctionalUnitUnits";
import {
  CF_AUTO_CALC_TITLE,
  isEmbodiedCarbonCharacterizationFactorUnitField,
  shouldDisableAutoCalculatedCharacterizationFactor,
} from "../../shared/contributorCharacterizationFactorUi";
import type { ReferenceLabels } from "../../../../../../components/submission/readonlyFormDisplay";

type RemoveConfirmState =
  | {
      kind: "vehicle";
      materialIndex: number;
      slotIndex: number;
      rawMaterialLabel: string;
      slotLabel: string;
    }
  | {
      kind: "fuel";
      materialIndex: number;
      slotIndex: number;
      rawMaterialLabel: string;
      slotLabel: string;
    };

interface RawMat {
  id?: number;
  idx: number;
  name: string;
}

interface Props {
  section: FormSubSection;
  rowCount: number;
  rawMaterials: RawMat[];
  values: Record<string, string>;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  /** Length === rawMaterials.length; vehicles per background raw material (A2 vehicle section only). */
  vehiclesPerRawMaterial?: number[];
  onAddVehicleForMaterial?: (materialIndex: number) => void;
  onRemoveVehicleForMaterial?: (
    materialIndex: number,
    vehicleSlotIndex: number
  ) => void;
  /** Material-level transport fuels per raw material (A2 fuel section only). */
  fuelsPerRawMaterial?: number[];
  onAddFuelForMaterial?: (materialIndex: number) => void;
  onRemoveFuelForMaterial?: (
    materialIndex: number,
    fuelSlotIndex: number
  ) => void;
  /** Embodied energy fields (from A2_EMBODIED_ENERGY CMS) rendered under each fuel row; value keys use A2_FUEL_TRANSPORT. */
  embodiedEnergyFieldsForFuel?: FormField[];
  /** When set, Vehicle Category uses API ids (String(row.id)) and shows emission factor help text. */
  vehicleCategoryEmissionFactors?: VehicleCategoryEmissionFactorPublic[];
  /** Dataset Overview: all controls disabled; hide add/remove actions. */
  readonly?: boolean;
  referenceLabels?: ReferenceLabels;
  errors?: Record<string, string>;
  formData?: FormDataResponse | null;
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
  backgroundFinishedMaterialUnitSymbol?: string | null;
}

const RowSeparator = () => (
  <div className="a1-row-separator" style={{ height: 1 }} />
);

/** Same pattern as Form A1: one numbered heading per raw-material row in each subsection. */
const rawMaterialTripLabel = (rowIndex: number) =>
  `Raw Material ${String(rowIndex + 1).padStart(2, "0")}`;

/** CMS may use a static label like "Raw Material 01" on every row; strip trailing digits so the h4 carries the count. */
function displayFieldLabel(field: FormField): string {
  let label = resolveContributorFieldDisplayLabel(field);
  if (field.type === "RAW_MATERIALS") {
    label = label.replace(/\s+\d{1,2}\s*$/, "").trim();
    if (!label) label = "Raw material";
  }
  return label;
}

interface UnitOption extends ContributorUnitOption {
  name: string;
}

const normalizeUnitToken = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

function buildEmbodiedEnergyUnitOptions(
  units: UnitOption[],
  field: FormField
): Array<{ value: string; label: string }> {
  if (units.length > 0) {
    return units.map((unit) => ({
      value: String(unit.id),
      label: formatUnitOptionLabel(unit),
    }));
  }

  const dropdown = field.dropdown?.options || field.dropdownValues || [];
  if (dropdown.length > 0) {
    return dropdown.map((opt: { value?: string | number; label?: string }) => ({
      value: String(opt.value),
      label: String(opt.label ?? opt.value),
    }));
  }

  const pick = (tokens: string[]) => {
    for (const unit of units) {
      const candidates = [unit.unitSymbol, unit.symbol, unit.name, unit.displayName];
      for (const candidate of candidates) {
        const normalized = normalizeUnitToken(candidate);
        if (tokens.some((token) => normalized === normalizeUnitToken(token))) {
          return {
            value: String(unit.id),
            label: formatUnitOptionLabel(unit),
          };
        }
      }
    }
    return null;
  };

  const resolved: Array<{ value: string; label: string }> = [];
  const kwh = pick(["kwh", "kilowatt-hour", "kilowatthour"]);
  const mj = pick(["mj", "megajoule"]);
  if (kwh) resolved.push(kwh);
  if (mj) resolved.push(mj);
  if (resolved.length > 0) return resolved;

  return [
    { value: "kWh", label: "kWh" },
    { value: "MJ", label: "MJ" },
  ];
}

function resolveEmbodiedEnergyUnits(
  mappedUnits: UnitOption[],
  allUnits: UnitOption[]
): UnitOption[] {
  if (mappedUnits.length > 0) return mappedUnits;

  return allUnits.filter((unit) => {
    const normalized = normalizeUnitToken(
      unit.unitSymbol || unit.symbol || unit.name || unit.displayName
    );
    return ["kwh", "kilowatt-hour", "kilowatthour", "mj", "megajoule"].some(
      (token) => normalized === normalizeUnitToken(token)
    );
  });
}

const A2Section: React.FC<Props> = ({
  section,
  rowCount,
  rawMaterials,
  values,
  onChange,
  vehiclesPerRawMaterial,
  onAddVehicleForMaterial,
  onRemoveVehicleForMaterial,
  fuelsPerRawMaterial,
  onAddFuelForMaterial,
  onRemoveFuelForMaterial,
  embodiedEnergyFieldsForFuel,
  vehicleCategoryEmissionFactors,
  readonly = false,
  referenceLabels,
  errors = {},
  formData = null,
  fuelDensityCatalog = [],
  calorificValuesCatalog = [],
  backgroundFinishedMaterialUnitSymbol = null,
}) => {
  const code = section.code || section.name || String(section.id);
  const [unitOptionsMap, setUnitOptionsMap] = useState<Record<number, UnitOption[]>>({});
  const [allUnitsFlat, setAllUnitsFlat] = useState<UnitOption[]>([]);
  const [removeConfirm, setRemoveConfirm] = useState<RemoveConfirmState | null>(
    null
  );

  // Fetch units for all UNIT fields in this section (and embodied-energy fields rendered under fuel rows)
  useEffect(() => {
    const fetchUnits = async () => {
      const unitFields = [
        ...section.fields.filter((f) => f.type === "UNIT" && f.unitPropertyId),
        ...(embodiedEnergyFieldsForFuel ?? []).filter(
          (f) => f.type === "UNIT" && f.unitPropertyId
        ),
      ].filter((field, index, list) => {
        return list.findIndex((item) => item.id === field.id) === index;
      });

      const promises = unitFields.map(async (field) => {
        if (!field.unitPropertyId) return;
        try {
          const res = await axiosClient.get(API_URLS.UNITS.BY_PROPERTY(field.unitPropertyId));
          if (res.data?.success && res.data?.data) {
            const units = Array.isArray(res.data.data) ? res.data.data : [];
            return { fieldId: field.id, units };
          }
        } catch (err) {
          console.warn(`Failed to fetch units for property ${field.unitPropertyId}:`, err);
        }
        return { fieldId: field.id, units: [] };
      });

      const results = await Promise.all(promises);
      const newMap: Record<number, UnitOption[]> = {};
      results.forEach((result) => {
        if (result) {
          newMap[result.fieldId] = result.units;
        }
      });
      setUnitOptionsMap(newMap);
    };

    const hasUnitFields =
      section.fields.some((f) => f.type === "UNIT" && f.unitPropertyId) ||
      (embodiedEnergyFieldsForFuel ?? []).some(
        (f) => f.type === "UNIT" && f.unitPropertyId
      );

    if (hasUnitFields) {
      fetchUnits();
    }
  }, [section, embodiedEnergyFieldsForFuel]);

  // Fetch ALL units once (needed for embodied energy unit fallback options)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get("/units");
        const raw = res?.data?.data ?? res?.data;

        const flat: UnitOption[] = [];
        const consumeUnit = (u: any) => {
          if (!u?.id) return;
          flat.push({
            id: Number(u.id),
            name: u.name || u.displayName || u.unitSymbol || u.symbol || "",
            symbol: u.symbol || u.unitSymbol,
            unitSymbol: u.unitSymbol,
            displayName: u.displayName,
          });
        };

        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit: any) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units.forEach(consumeUnit);
            } else {
              consumeUnit(propertyOrUnit);
            }
          });
        } else if (raw && Array.isArray((raw as any).units)) {
          (raw as any).units.forEach(consumeUnit);
        }

        if (!cancelled) setAllUnitsFlat(flat.filter((u) => Number.isFinite(u.id)));
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getField = (name: string) =>
    section.fields.find((f) => f.name === name);

  type RowOpts = {
    lockedRawMaterialIndex?: number;
    /** Second+ vehicle/fuel row for the same raw material — field only, no duplicate label. */
    suppressRawMaterialFieldLabel?: boolean;
  };

  const renderInput = (f: FormField, rowIndex: number, opts?: RowOpts) => {
    const fieldName = `${f.name}_${code}_row${rowIndex}`;
    const cfLocked = shouldDisableAutoCalculatedCharacterizationFactor(code, f, {
      values,
      rowIndex,
      formData,
      vehiclesPerRawMaterial: vehiclesPerRawMaterial ?? [],
      vehicleCategoryEmissionFactors: vehicleCategoryEmissionFactors ?? [],
    });
    const isVehicleNumericWithDefaultZero =
      f.name === "tripsRequired" || f.name === "totalDistanceTravelled";
    const valueOrDefault = isVehicleNumericWithDefaultZero
      ? (values[fieldName] || "0")
      : (values[fieldName] || "");
    
    // Disable state and district fields (auto-populated from lat/lng)
    const forceEditable = [
      "embodiedCarbon_referenceQty_unit",
      "embodiedCarbon_functionalUnit_unit",
      "totalDistanceTravelled",
      "tripsRequired",
    ].includes(f.name);

    const isDisabled =
      readonly ||
      ((f.type === "READONLY" ||
        f.isEditable === false ||
        f.name === "state" ||
        f.name === "district") &&
        !forceEditable);

    if (
      f.name === "manufacturingLatitude" ||
      f.name === "manufacturingLongitude"
    ) {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={values[fieldName] || ""}
          onChange={onChange}
          onBlur={onChange}
          readOnly={isDisabled}
          disabled={isDisabled}
        />
      );
    }

    if (f.name === "distanceBetweenSites") {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={values[fieldName] || ""}
          onChange={onChange}
          onBlur={onChange}
          readOnly={isDisabled}
          disabled={isDisabled}
          title="Distance in km from A1 extraction site to this manufacturing site. You can type a value, or clear the field to fill from coordinates (haversine) when lat/lng are set."
        />
      );
    }

    if (
      cfLocked &&
      !isEmbodiedCarbonCharacterizationFactorUnitField(f, code, formData)
    ) {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={values[fieldName] || ""}
          readOnly
          disabled
          className="readonly-input"
          title={CF_AUTO_CALC_TITLE}
        />
      );
    }

    if (
      cfLocked &&
      isEmbodiedCarbonCharacterizationFactorUnitField(f, code, formData)
    ) {
      const display =
        resolveContributorUnitDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          propertyUnits: unitOptionsMap[f.id] || [],
          allUnitsFlat,
          dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
          referenceUnits: referenceLabels?.units,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
          title={CF_AUTO_CALC_TITLE}
        />
      );
    }

    if (f.name === "tripsRequired" || f.name === "totalDistanceTravelled") {
      let nVehiclesForThisMaterial = 1;
      if (
        code === "A2_VEHICLE_DETAILS" &&
        vehiclesPerRawMaterial &&
        opts?.lockedRawMaterialIndex != null
      ) {
        const mi = opts.lockedRawMaterialIndex;
        if (mi >= 0 && mi < vehiclesPerRawMaterial.length) {
          nVehiclesForThisMaterial = Math.max(
            1,
            vehiclesPerRawMaterial[mi] ?? 1
          );
        }
      }
      const multiVehicle =
        code === "A2_VEHICLE_DETAILS" && nVehiclesForThisMaterial > 1;

      if (f.name === "totalDistanceTravelled") {
        return (
          <input
            type="text"
            inputMode="decimal"
            name={fieldName}
            value={valueOrDefault}
            onChange={onChange}
            onBlur={onChange}
            readOnly={isDisabled}
            disabled={isDisabled}
            title={
              multiVehicle
                ? "Total distance travelled for transporting this raw material (km) is not calculated automatically when more than one vehicle is used — enter the value for this vehicle. Used for embodied carbon (not derived from site-to-site distance × trips)."
                : "Calculated as number of trips × distance between extraction and manufacturing (km). You may edit this value if needed."
            }
          />
        );
      }

      if (multiVehicle) {
        return (
          <input
            type="text"
            inputMode="decimal"
            name={fieldName}
            value={values[fieldName] ?? ""}
            onChange={onChange}
            onBlur={onChange}
            readOnly={isDisabled}
            disabled={isDisabled}
            title="Enter trips for this vehicle. Not calculated automatically when more than one transport vehicle is used for this raw material."
          />
        );
      }

      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={valueOrDefault}
          onChange={onChange}
          onBlur={onChange}
          readOnly={isDisabled}
          disabled={isDisabled}
          title="Calculated: ceil(declared quantity from Background ÷ vehicle capacity). You may edit this value if needed."
        />
      );
    }

    if (f.type === "RAW_MATERIALS") {
      if (opts?.lockedRawMaterialIndex != null) {
        const lm = opts.lockedRawMaterialIndex;
        const rm = rawMaterials[lm];
        if (!rm) {
          return (
            <select name={fieldName} value="" disabled>
              <option value="">—</option>
            </select>
          );
        }
        const val =
          values[fieldName] || String(rm.id ?? rm.idx ?? "");
        return (
          <select name={fieldName} value={val} disabled>
            <option value={String(rm.id ?? rm.idx ?? "")}>{rm.name}</option>
          </select>
        );
      }
      return (
        <select name={fieldName} value={values[fieldName] || ""} disabled>
          <option value="">Select Raw Material...</option>
          {rawMaterials.map((rm) => (
            <option
              key={rm.idx}
              value={String(rm.id ?? rm.idx)}
            >
              {rm.name}
            </option>
          ))}
        </select>
      );
    }

    if (readonly && f.type === "UNIT") {
      const display =
        resolveContributorUnitDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          propertyUnits: unitOptionsMap[f.id] || [],
          allUnitsFlat,
          dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
          referenceUnits: referenceLabels?.units,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
        />
      );
    }

    if (readonly && f.type === "DROPDOWN") {
      const isVehicleCategory = f.name === "vehicleCategory";
      const display =
        resolveContributorDropdownDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          options:
            isVehicleCategory && vehicleCategoryEmissionFactors?.length
              ? vehicleCategoryEmissionFactors.map((r) => ({
                  value: r.id,
                  label: r.categoryLabel,
                }))
              : f.dropdown?.options || f.dropdownValues || [],
          referenceMap: isVehicleCategory
            ? referenceLabels?.vehicleCategories
            : undefined,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
        />
      );
    }

    if (f.type === "DROPDOWN") {
      if (
        f.name === "vehicleCategory" &&
        code === "A2_VEHICLE_DETAILS" &&
        vehicleCategoryEmissionFactors?.length
      ) {
        const rows = vehicleCategoryEmissionFactors;
        const raw = values[fieldName] ?? "";
        const matched = findVehicleCategoryEmissionFactor(String(raw), rows);
        const idSet = new Set(rows.map((r) => String(r.id)));
        const selectValue =
          matched && idSet.has(String(matched.id))
            ? String(matched.id)
            : idSet.has(String(raw).trim())
              ? String(raw).trim()
              : "";
        const hintRow =
          selectValue !== ""
            ? rows.find((r) => String(r.id) === selectValue)
            : undefined;

        return (
          <div className="a2-vehicle-category-field">
            <select
              name={fieldName}
              value={selectValue}
              onChange={onChange}
              disabled={isDisabled}
            >
              <option value="">Select...</option>
              {rows.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.categoryLabel}
                </option>
              ))}
            </select>
            {hintRow ? (
              <p className="a2-vehicle-category-emission-hint" role="note">
                Emission factor: {hintRow.emissionFactor} {hintRow.unit}
              </p>
            ) : null}
          </div>
        );
      }

      return (
        <select
          name={fieldName}
          value={values[fieldName] || ""}
          onChange={onChange}
          disabled={isDisabled}
        >
          <option value="">Select...</option>
          {f.dropdown?.options?.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (f.type === "UNIT") {
      if (f.name === "embodiedEnergy_unit") {
        const mappedUnits = resolveEmbodiedEnergyUnits(
          unitOptionsMap[f.id] || [],
          allUnitsFlat
        );
        if (mappedUnits.length > 0) {
          const { options, selectValue } = buildContributorUnitSelectOptions({
            propertyUnits: mappedUnits,
            allUnitsFlat,
            dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
            selected: String(values[fieldName] ?? ""),
          });
          return (
            <select
              name={fieldName}
              value={selectValue}
              onChange={onChange}
              disabled={isDisabled}
            >
              <option value="">Select Unit...</option>
              {options.map((opt) => (
                <option key={String(opt.value)} value={String(opt.value)}>
                  {opt.label ?? String(opt.value)}
                </option>
              ))}
            </select>
          );
        }

        const tokenOptions = buildEmbodiedEnergyUnitOptions([], f);
        const selectValue = String(values[fieldName] ?? "").trim();
        const embodiedEnergyValue = tokenOptions.some(
          (opt) => opt.value === selectValue
        )
          ? selectValue
          : "";

        return (
          <select
            name={fieldName}
            value={embodiedEnergyValue}
            onChange={onChange}
            disabled={isDisabled}
          >
            <option value="">Select Unit...</option>
            {tokenOptions.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label ?? String(opt.value)}
              </option>
            ))}
          </select>
        );
      }

      if (isEmbodiedCarbonFunctionalUnitFieldName(f.name)) {
        const { options, selectValue } =
          buildEmbodiedCarbonFunctionalUnitSelectState(
            backgroundFinishedMaterialUnitSymbol ?? null,
            String(values[fieldName] ?? "")
          );
        if (readonly) {
          const display =
            resolveEmbodiedCarbonFunctionalUnitDisplayLabel(
              backgroundFinishedMaterialUnitSymbol ?? null,
              selectValue
            ) || "—";
          return (
            <input
              type="text"
              className="readonly-input"
              value={display}
              readOnly
              disabled
            />
          );
        }
        return (
          <select
            name={fieldName}
            value={selectValue}
            onChange={onChange}
            disabled={isDisabled}
          >
            <option value="">Select Unit...</option>
            {options.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      const { options, selectValue } = buildContributorUnitSelectOptions({
        propertyUnits: unitOptionsMap[f.id] || [],
        allUnitsFlat,
        dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
        selected: String(values[fieldName] ?? ""),
      });
      const unitOptions = isFuelDensityUnitFormFieldName(f.name)
        ? filterFuelDensityUnitOptionsToKgPerM3(options)
        : options;
      const unitSelectValue = unitOptions.some(
        (opt) => opt.value === selectValue
      )
        ? selectValue
        : unitOptions.length === 1
          ? unitOptions[0].value
          : "";

      return (
        <select
          name={fieldName}
          value={unitSelectValue}
          onChange={onChange}
          disabled={isDisabled}
        >
          <option value="">Select Unit...</option>
          {unitOptions.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label ?? String(opt.value)}
            </option>
          ))}
        </select>
      );
    }

    if (f.type === "READONLY") {
      return (
        <input
          type="text"
          className="readonly-input"
          value={valueOrDefault}
          readOnly
        />
      );
    }

    return (
      <input
        {...getContributorTextInputHtmlProps(f)}
        name={fieldName}
        value={valueOrDefault}
        onChange={onChange}
        disabled={isDisabled}
      />
    );
  };

  const resolveFieldLabel = (
    field: FormField,
    rowIndex: number,
    opts?: RowOpts
  ): string => {
    if (field.type === "RAW_MATERIALS") {
      const materialIndex = opts?.lockedRawMaterialIndex ?? rowIndex;
      return rawMaterialTripLabel(materialIndex);
    }
    return displayFieldLabel(field);
  };

  const renderLabeled = (
    field?: FormField,
    rowIndex = 0,
    opts?: RowOpts
  ) => {
    if (!field) return null;
    if (
      field.type === "RAW_MATERIALS" &&
      opts?.suppressRawMaterialFieldLabel
    ) {
      return renderInput(field, rowIndex, opts);
    }
    const fieldName = `${field.name}_${code}_row${rowIndex}`;
    const fieldError = errors[fieldName];
    return (
      <div className={fieldError ? "field-has-error" : undefined}>
        <FormFieldLabel
          label={resolveFieldLabel(field, rowIndex, opts)}
          required={resolveContributorFieldMandatory({
            field,
            sectionCode: code,
            rowIndex,
            values,
            formData,
            fuelDensityCatalog,
            calorificValuesCatalog,
          })}
          infoText={field.infoText}
        />
        {renderInput(field, rowIndex, opts)}
        {fieldError ? (
          <span className="error-message">{fieldError}</span>
        ) : null}
      </div>
    );
  };

  /* ===================== A2 TRANSPORT ===================== */
  if (code === "A2_TRANSPORT_DETAILS") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, i) => (
          <React.Fragment key={i}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterialTransportDetails"),
                  i
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("manufacturingLatitude"), i)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("manufacturingLongitude"), i)}
              </div>

              <div className="field-cell empty-cell" />

              <div className="field-cell">
                {renderLabeled(getField("state"), i)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("district"), i)}
              </div>

              <div className="field-cell empty-cell" />

              <div className="field-cell">
                {renderLabeled(getField("pincode"), i)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("distanceBetweenSites"), i)}
              </div>
            </div>

            {i < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  /* ===================== A2 VEHICLE =====================
     Multiple vehicle rows per raw material; flat form row indices match save order. */
  if (code === "A2_VEHICLE_DETAILS") {
    if (rawMaterials.length === 0) {
      return (
        <>
          <p className="a2-fuel-types-note">
            Please note, up to {A2_MAX_VEHICLES_PER_RAW_MATERIAL} transport
            vehicles can be added per raw material.
          </p>
          {Array.from({ length: rowCount }).map((_, i) => (
            <React.Fragment key={i}>
              <div className="a1-raw-extraction-grid">
                <div className="field-cell">
                  {renderLabeled(getField("vehicle_rawMaterial_01"), i)}
                </div>
                <div className="field-cell">
                  {renderLabeled(getField("vehicleMake"), i)}
                </div>
                <div className="field-cell">
                  {renderLabeled(getField("vehicleModel"), i)}
                </div>
                <div className="field-cell empty-cell" />
                <div className="field-cell">
                  {renderLabeled(getField("vehicleCapacity"), i)}
                </div>
                <div className="field-cell">
                  {renderLabeled(getField("vehicleCategory"), i)}
                </div>
                <div className="field-cell empty-cell" />
                <div className="field-cell">
                  {renderLabeled(getField("tripsRequired"), i)}
                </div>
                <div className="field-cell">
                  {renderLabeled(getField("totalDistanceTravelled"), i)}
                </div>
              </div>
              {i < rowCount - 1 && <RowSeparator />}
            </React.Fragment>
          ))}
        </>
      );
    }

    const counts =
      vehiclesPerRawMaterial &&
      vehiclesPerRawMaterial.length === rawMaterials.length
        ? vehiclesPerRawMaterial.map((c) =>
            Math.min(A2_MAX_VEHICLES_PER_RAW_MATERIAL, Math.max(1, c))
          )
        : rawMaterials.map(() => 1);

    let flat = 0;
    return (
      <>
        <p className="a2-fuel-types-note">
          Please note, up to {A2_MAX_VEHICLES_PER_RAW_MATERIAL} transport
          vehicles can be added per raw material.
        </p>
        {rawMaterials.map((_, m) => {
          const n = Math.max(1, counts[m] ?? 1);
          return (
            <React.Fragment key={`a2-veh-mat-${m}`}>
              {Array.from({ length: n }).map((_, v) => {
                const rowIdx = flat;
                flat += 1;
                return (
                  <React.Fragment key={`a2-veh-m${m}-v${v}`}>
                    <div className="a2-repeatable-slot-header">
                      <h5 className="a2-repeatable-slot-title">
                        Vehicle {String(v + 1).padStart(2, "0")}
                      </h5>
                      {onRemoveVehicleForMaterial && n > 1 ? (
                        <button
                          type="button"
                          className="a2-remove-slot-btn"
                          onClick={() =>
                            setRemoveConfirm({
                              kind: "vehicle",
                              materialIndex: m,
                              slotIndex: v,
                              rawMaterialLabel: rawMaterialTripLabel(m),
                              slotLabel: `Vehicle ${String(v + 1).padStart(2, "0")}`,
                            })
                          }
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="a1-raw-extraction-grid">
                      <div className="field-cell">
                        {renderLabeled(
                          getField("vehicle_rawMaterial_01"),
                          rowIdx,
                          {
                            lockedRawMaterialIndex: m,
                            suppressRawMaterialFieldLabel: v > 0,
                          }
                        )}
                      </div>
                      <div className="field-cell">
                        {renderLabeled(getField("vehicleMake"), rowIdx)}
                      </div>
                      <div className="field-cell">
                        {renderLabeled(getField("vehicleModel"), rowIdx)}
                      </div>

                      <div className="field-cell empty-cell" />

                      <div className="field-cell">
                        {renderLabeled(getField("vehicleCapacity"), rowIdx)}
                      </div>
                      <div className="field-cell">
                        {renderLabeled(getField("vehicleCategory"), rowIdx)}
                      </div>

                      <div className="field-cell empty-cell" />

                      <div className="field-cell">
                        {renderLabeled(getField("tripsRequired"), rowIdx, {
                          lockedRawMaterialIndex: m,
                        })}
                      </div>
                      <div className="field-cell">
                        {renderLabeled(
                          getField("totalDistanceTravelled"),
                          rowIdx,
                          { lockedRawMaterialIndex: m }
                        )}
                      </div>
                    </div>
                    {v < n - 1 && <RowSeparator />}
                  </React.Fragment>
                );
              })}
              {onAddVehicleForMaterial ? (
                <div className="a2-add-vehicle-wrap">
                  <button
                    type="button"
                    className="add-row-btn"
                    disabled={n >= A2_MAX_VEHICLES_PER_RAW_MATERIAL}
                    onClick={() => onAddVehicleForMaterial(m)}
                  >
                    Add new vehicle
                  </button>
                </div>
              ) : null}
              {m < rawMaterials.length - 1 && <RowSeparator />}
            </React.Fragment>
          );
        })}
        <ConfirmModal
          open={removeConfirm?.kind === "vehicle"}
          title="Remove vehicle?"
          description={
            removeConfirm?.kind === "vehicle"
              ? `Remove ${removeConfirm.slotLabel} under ${removeConfirm.rawMaterialLabel}? Any data entered for this vehicle will be discarded.`
              : ""
          }
          confirmText="Remove vehicle"
          cancelText="Cancel"
          confirmButtonVariant="danger"
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={() => {
            if (
              removeConfirm?.kind === "vehicle" &&
              onRemoveVehicleForMaterial
            ) {
              onRemoveVehicleForMaterial(
                removeConfirm.materialIndex,
                removeConfirm.slotIndex
              );
            }
            setRemoveConfirm(null);
          }}
        />
      </>
    );
  }

  /* ===================== A2 FUEL =====================
     Multiple fuel rows per raw material; embodied energy optional per fuel row. */
  if (code === "A2_FUEL_TRANSPORT") {
    const energyFields = embodiedEnergyFieldsForFuel ?? [];

    const renderFuelRowGrid = (
      rowIdx: number,
      lockedM: number,
      fuelSlotIndex: number
    ) => (
      <div className="a1-raw-extraction-grid">
        <div className="field-cell">
          {renderLabeled(getField("fuel_rawMaterial_01"), rowIdx, {
            lockedRawMaterialIndex: lockedM,
            suppressRawMaterialFieldLabel: fuelSlotIndex > 0,
          })}
        </div>
        <div className="field-cell">
          {renderLabeled(getField("fuelMix_01"), rowIdx)}
        </div>

        <div className="field-cell empty-cell" />
        <div className="field-cell empty-cell" />

        <div className="field-cell">
          {renderLabeled(getField("fuelQuantity"), rowIdx)}
        </div>
        <div className="field-cell">
          {renderLabeled(getField("fuelQuantity_unit"), rowIdx)}
        </div>

        <div className="field-cell empty-cell" />

        <div className="field-cell">
          {renderLabeled(getField("fuelDensity"), rowIdx)}
        </div>
        <div className="field-cell">
          {renderLabeled(getField("fuelDensity_unit"), rowIdx)}
        </div>

        <div className="field-cell empty-cell" />

        <div className="field-cell">
          {renderLabeled(getField("calorificValue"), rowIdx)}
        </div>
        <div className="field-cell">
          {renderLabeled(getField("calorificValue_unit"), rowIdx)}
        </div>

        {energyFields.length > 0 ? (
          <>
            <div className="field-cell empty-cell" />
            {energyFields.map((ef) => (
              <div className="field-cell" key={`${ef.name}-${rowIdx}`}>
                {renderLabeled(ef, rowIdx)}
              </div>
            ))}
          </>
        ) : null}
      </div>
    );

    if (rawMaterials.length === 0) {
      return (
        <>
          <p className="a2-fuel-types-note">
            Please note, upto 3 fuel types can be added per raw material.
          </p>
          {Array.from({ length: rowCount }).map((_, i) => (
            <React.Fragment key={i}>
              {renderFuelRowGrid(i, i, 0)}
              {i < rowCount - 1 && <RowSeparator />}
            </React.Fragment>
          ))}
        </>
      );
    }

    const fCounts =
      fuelsPerRawMaterial &&
      fuelsPerRawMaterial.length === rawMaterials.length
        ? fuelsPerRawMaterial.map((c) =>
            Math.min(A2_MAX_FUELS_PER_RAW_MATERIAL, Math.max(1, c))
          )
        : rawMaterials.map(() => 1);

    let flat = 0;
    return (
      <>
        <p className="a2-fuel-types-note">
          Please note, upto 3 fuel types can be added per raw material.
        </p>
        {rawMaterials.map((_, m) => {
          const n = Math.max(1, fCounts[m] ?? 1);
          return (
            <React.Fragment key={`a2-fuel-mat-${m}`}>
              {Array.from({ length: n }).map((_, f) => {
                const rowIdx = flat;
                flat += 1;
                return (
                  <React.Fragment key={`a2-fuel-m${m}-f${f}`}>
                    <div className="a2-repeatable-slot-header">
                      <h5 className="a2-repeatable-slot-title">
                        Fuel type {String(f + 1).padStart(2, "0")}
                      </h5>
                      {onRemoveFuelForMaterial && n > 1 ? (
                        <button
                          type="button"
                          className="a2-remove-slot-btn"
                          onClick={() =>
                            setRemoveConfirm({
                              kind: "fuel",
                              materialIndex: m,
                              slotIndex: f,
                              rawMaterialLabel: rawMaterialTripLabel(m),
                              slotLabel: `Fuel type ${String(f + 1).padStart(2, "0")}`,
                            })
                          }
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    {renderFuelRowGrid(rowIdx, m, f)}
                    {f < n - 1 && <RowSeparator />}
                  </React.Fragment>
                );
              })}
              {onAddFuelForMaterial ? (
                <div className="a2-add-vehicle-wrap">
                  <button
                    type="button"
                    className="add-row-btn"
                    disabled={n >= A2_MAX_FUELS_PER_RAW_MATERIAL}
                    onClick={() => onAddFuelForMaterial(m)}
                  >
                    Add fuel type
                  </button>
                </div>
              ) : null}
              {m < rawMaterials.length - 1 && <RowSeparator />}
            </React.Fragment>
          );
        })}
        <ConfirmModal
          open={removeConfirm?.kind === "fuel"}
          title="Remove fuel type?"
          description={
            removeConfirm?.kind === "fuel"
              ? `Remove ${removeConfirm.slotLabel} under ${removeConfirm.rawMaterialLabel}? Any data entered for this fuel row will be discarded.`
              : ""
          }
          confirmText="Remove fuel type"
          cancelText="Cancel"
          confirmButtonVariant="danger"
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={() => {
            if (
              removeConfirm?.kind === "fuel" &&
              onRemoveFuelForMaterial
            ) {
              onRemoveFuelForMaterial(
                removeConfirm.materialIndex,
                removeConfirm.slotIndex
              );
            }
            setRemoveConfirm(null);
          }}
        />
      </>
    );
  }

  /* ===================== A2 ENERGY ===================== */
  if (code === "A2_EMBODIED_ENERGY") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, i) => (
          <React.Fragment key={i}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterialEmbodiedEnergyDetails"),
                  i
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("embodiedEnergy"), i)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("embodiedEnergy_unit"), i)}
              </div>
            </div>

            {i < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  /* ===================== A2 CARBON ===================== */
  if (code === "A2_EMBODIED_CARBON") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, i) => (
          <React.Fragment key={i}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterialEmbodiedCarbonDetails"),
                  i
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("characterizationFactor"), i)}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("characterizationFactor_unit"),
                  i
                )}
              </div>

              <div className="field-cell empty-cell" />

              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedCarbon_referenceQty"),
                  i
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedCarbon_referenceQty_unit"),
                  i
                )}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedCarbon_functionalUnit"),
                  i
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedCarbon_functionalUnit_unit"),
                  i
                )}
              </div>
            </div>

            {i < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  return null;
};

/** React component — A2Section. */
export default A2Section;